import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys } from '../lib/query-client';
import { AuthState, SessionQuery } from '../types';

// Auth hooks (Auth0 only)
export const useAuth = () => {
  return useQuery({
    queryKey: queryKeys.auth.verify(),
    queryFn: async (): Promise<AuthState> => {
      try {
        const response = await apiService.getAuth0Session();
        if (response.success && response.data) {
          return {
            isAuthenticated: true,
            session: response.data,
          };
        }
      } catch {
        // Fall through to unauthenticated state on any error
      }

      return {
        isAuthenticated: false,
        session: null,
      };
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: { returnTo?: string } = {}) => {
      const url = await apiService.auth0Logout(options.returnTo);
      return url;
    },
    onSuccess: (url) => {
      queryClient.clear();
      if (url) {
        window.location.href = url;
      }
    },
  });
};

// Projects hooks
export const useProjects = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.projects.lists(),
    queryFn: () => apiService.getProjects(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled,
  });
};

export const useProject = (id: string) => {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => apiService.getProjectById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      apiService.createProject(data),
    onSuccess: () => {
      // Invalidate projects list
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiService.deleteProject(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache and invalidate lists
      queryClient.removeQueries({ queryKey: queryKeys.projects.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() });
      // Also invalidate sessions since they might be project-specific
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.lists() });
    },
  });
};

// Sessions hooks
export const useSessions = (query: SessionQuery = {}) => {
  return useQuery({
    queryKey: queryKeys.sessions.list(query),
    queryFn: () => apiService.getSessions(query),
    staleTime: 30 * 1000, // 30 seconds for sessions (more dynamic)
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Keep previous data while fetching new data
    placeholderData: (previousData) => previousData,
  });
};

export const useAllSessions = (query: Omit<SessionQuery, 'projectId'> = {}) => {
  return useQuery({
    queryKey: queryKeys.sessions.list({ ...query, allSessions: true }),
    queryFn: () => apiService.getAllSessions(query),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};

export const useSession = (sessionId: string, projectId: string) => {
  return useQuery({
    queryKey: queryKeys.sessions.detail(sessionId, projectId),
    queryFn: () => apiService.getSessionById(sessionId, projectId),
    enabled: !!sessionId && !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ sessionId, projectId }: { sessionId: string; projectId: string }) =>
      apiService.deleteSession(sessionId, projectId),
    onSuccess: (_, { sessionId, projectId }) => {
      // Remove from cache
      queryClient.removeQueries({ 
        queryKey: queryKeys.sessions.detail(sessionId, projectId) 
      });
      // Invalidate sessions lists
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.lists() });
    },
  });
};

// Per-session issues — used by SessionDetailPage to drive the friction
// surface. Backend returns paginated UxIssue rows; we expose just the array.
export const useIssues = (
  params: { projectId: string; sessionId: string },
  options?: { enabled?: boolean },
) => {
  const enabled = !!params.projectId && !!params.sessionId && (options?.enabled ?? true);
  return useQuery({
    queryKey: [...queryKeys.issues.all, 'session', params.projectId, params.sessionId] as const,
    queryFn: async () => {
      const response = await apiService.getIssues({
        projectId: params.projectId,
        sessionId: params.sessionId,
      });
      return response.data ?? [];
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Variants linked to a list of issue IDs. We fetch each issue's variants
 * with `getVariants({ projectId, issueId })` and shallow-merge into a single
 * map keyed by issueId. Used by IssuesPage rows to surface the
 * proposal-engine pipeline state per issue. Disabled until at least one
 * issueId is present; cached per project + sorted-id signature.
 */
export const useIssuesVariants = (
  params: { projectId: string | null | undefined; issueIds: string[] },
  options?: { enabled?: boolean },
) => {
  const enabled = !!params.projectId && params.issueIds.length > 0 && (options?.enabled ?? true);
  const sig = params.issueIds.slice().sort().join('|');
  return useQuery({
    queryKey: ['variants', 'byIssue', params.projectId || '__all__', sig] as const,
    queryFn: async () => {
      if (!params.projectId) return {} as Record<string, import('../types').Variant[]>;
      const out: Record<string, import('../types').Variant[]> = {};
      // Fan out — backend doesn't expose a `?issueId[]=` shape today, so we
      // make N parallel requests. With <20 visible issues per page this is
      // fine; if the page ever shows 100+ rows we should add a bulk endpoint.
      const results = await Promise.all(
        params.issueIds.map((id) =>
          apiService
            .getVariants({ projectId: params.projectId as string, issueId: id, limit: 5 })
            .then((r) => ({ id, list: r.data || [] }))
            .catch(() => ({ id, list: [] as import('../types').Variant[] })),
        ),
      );
      for (const { id, list } of results) out[id] = list;
      return out;
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Issue hooks — used by SessionsPage Friction card and per-row enrichment.
// `projectId` is optional: when omitted, the backend rolls up across every
// project the caller can access (the cross-project / "All projects" view).
export const useIssueStats = (
  projectId: string | null | undefined,
  options?: { enabled?: boolean },
) => {
  const id = projectId || '';
  return useQuery({
    queryKey: queryKeys.issues.stats(id || '__all__'),
    queryFn: () => apiService.getIssueStats(id || undefined),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAggregatedIssues = (
  params: { projectId?: string | null; status?: string; limit?: number },
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.issues.aggregated(params.projectId || '__all__', params),
    queryFn: () =>
      apiService.getAggregatedIssues({
        projectId: params.projectId || undefined,
        status: params.status,
        limit: params.limit,
      }),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAutoRolloutQueue = (projectId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['autoRollout', 'queue', projectId],
    queryFn: () => apiService.getAutoRolloutQueue(projectId),
    enabled: !!projectId && (options?.enabled ?? true),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Journeys hooks
export const useJourneys = (
  params: {
    projectId?: string | null;
    frictionLevel?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
  },
  options?: { enabled?: boolean },
) => {
  const enabled = !!params.projectId && (options?.enabled ?? true);
  return useQuery({
    queryKey: ['journeys', 'list', params.projectId || '__all__', params],
    queryFn: () =>
      apiService.getJourneys({
        projectId: params.projectId as string,
        frictionLevel: params.frictionLevel,
        status: params.status,
        sortBy: params.sortBy ?? 'frictionScore',
        sortOrder: params.sortOrder ?? 'desc',
        limit: params.limit ?? 50,
      }),
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
};

export const useJourney = (id: string) => {
  return useQuery({
    queryKey: ['journeys', 'detail', id],
    queryFn: () => apiService.getJourney(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useJourneyStats = (
  projectId: string | null | undefined,
  options?: { enabled?: boolean },
) => {
  const id = projectId || '';
  return useQuery({
    queryKey: ['journeys', 'stats', id || '__all__'],
    queryFn: () => apiService.getJourneyStats(id),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAnalyzeJourneys = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; limit?: number; generateNarrative?: boolean }) =>
      apiService.analyzeJourneys(params.projectId, {
        generateNarrative: params.generateNarrative ?? true,
        limit: params.limit,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journeys'] });
    },
  });
};

export const useUpdateJourneyStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiService.updateJourneyStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['journeys', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['journeys', 'list'] });
    },
  });
};

// Experiments hooks
export const useExperiments = (
  params: { projectId: string | null | undefined; status?: string },
  options?: { enabled?: boolean },
) => {
  const enabled = !!params.projectId && (options?.enabled ?? true);
  return useQuery({
    queryKey: ['experiments', 'list', params.projectId, params.status ?? ''],
    queryFn: () =>
      apiService.getExperiments({
        projectId: params.projectId as string,
        status: params.status,
      }),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
};

export const useExperimentResults = (id: string | null | undefined) => {
  return useQuery({
    queryKey: ['experiments', 'detail', id ?? ''],
    queryFn: () => apiService.getExperimentResults(id as string),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useExperimentAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; action: 'start' | 'pause' | 'stop' }) => {
      if (params.action === 'start') return apiService.startExperiment(params.id);
      if (params.action === 'pause') return apiService.pauseExperiment(params.id);
      return apiService.stopExperiment(params.id);
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['experiments', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['experiments', 'detail', params.id] });
    },
  });
};


export const useGitHubAuthStatus = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['github', 'authStatus'],
    queryFn: () => apiService.getGitHubAuthStatus(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// API Key management hooks (Auth0 only)
export const useApiKeys = () => {
  return useQuery({
    queryKey: queryKeys.apiKeys.list(),
    queryFn: async () => {
      const response = await apiService.listApiKeys();
      return response.data ?? [];
    },
  });
};

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string; description?: string; label?: string }) =>
      apiService.createApiKey(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() });
    },
  });
};

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKeyId: string) => apiService.deleteApiKey(apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() });
    },
  });
};

// Utility hook for prefetching data
export const usePrefetchSession = () => {
  const queryClient = useQueryClient();
  
  return (sessionId: string, projectId: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.sessions.detail(sessionId, projectId),
      queryFn: () => apiService.getSessionById(sessionId, projectId),
      staleTime: 2 * 60 * 1000,
    });
  };
};

// Utility hook for prefetching sessions list
export const usePrefetchSessions = () => {
  const queryClient = useQueryClient();
  
  return (query: SessionQuery) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.sessions.list(query),
      queryFn: () => apiService.getSessions(query),
      staleTime: 30 * 1000,
    });
  };
}; 